import { DataTableCellAlertService } from '@/components/UI/DataTableV2/service/DataTableCellAlertService';
import { _isEmpty, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { AnimalAlertV1 } from '@/model/Alert.model';
import type { ID, ISODateTime, Nullable } from '@/model/Common.model';
import type { DataTableColumnFillRequestV1 } from '@/model/DataTable.model.ts';
import InVivoError from '@/model/InVivoError.ts';
import type { StudyApiId } from '@/model/Study.model';
import { AlertService, type AlertUpdateEvent } from '@/utils/alerts/useAlert';
import { structuredCloneUtil } from '@/utils/browser.utils';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { errorToast } from '@/helpers';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import axios from 'axios';
import {
  animationFrameScheduler,
  BehaviorSubject,
  buffer,
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  EMPTY,
  EmptyError,
  filter,
  firstValueFrom,
  forkJoin,
  from,
  fromEvent,
  map,
  merge,
  mergeMap,
  Observable,
  observeOn,
  of,
  retry,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import {
  DataTable,
  DataTableApiId,
  DataTableApiLoadCellResponse,
  DataTableCellAlertCache,
  DataTableCellAlertUpdateEventDetail,
  DataTableCellDataCache,
  DataTableCellIdCoordinate,
  DataTableCellIndexCoordinate,
  DataTableCellReference,
  DataTableCellSelectionChangeEvent,
  DataTableCellSelectionChangeEventDetail,
  DataTableCellSelectionOptions,
  DataTableCellState,
  DataTableCellStatus,
  DataTableCellStatusType,
  DataTableCellsUpdateOptions,
  DataTableCellUpdateEventDetail,
  DataTableCellUpsert,
  DataTableCellUpsertError,
  DataTableCellUpsertResponse,
  DataTableColumn,
  DataTableColumnAddRequest,
  DataTableColumnApiId,
  DataTableColumnExpansionRequest,
  DataTableColumnExpansionResponseAffectedColumns,
  DataTableColumnGroup,
  DataTableColumnGroupExpansionAction,
  DataTableColumnGroupingData,
  DataTableColumnIndex,
  DataTableColumnMoveRequest,
  DataTableColumnRemoveResponse,
  DataTableColumnType,
  DataTableColumnUpdateEventDetail,
  DataTableColumnUpdateRequest,
  DataTableDimensions,
  DataTableDirection,
  DataTableEvent,
  DataTableFeatureFlags,
  DataTableHighVolumeUpsertDetail,
  DataTableListener,
  DataTableMoveColumnResponse,
  DataTableMoveSelectionOptions,
  DataTablePasteLimitExceededEvent,
  DataTableRow,
  DataTableRowAddRequest,
  DataTableRowApiId,
  DataTableRowHeader,
  DataTableRowHeaderId,
  DataTableRowHeaderStateChangeEventDetail,
  DataTableRowIndex,
  DataTableSelection as IDataTableSelection,
  DataTableService as IDataTableService,
  DataTableServiceProps,
  DataTableSettingsUpdate,
  DataTableStateChangeEventDetail,
  DataTableStateChangeType,
  DataTableType,
  DataTableUpdateDisplayedRowHeadersOptions,
  DataTableUpsertDetail,
  DataTableValidateResponse,
  DataTableWindowClearRequest,
  DataTableWindowClearResponse,
  isUpdatableColumnType,
  PinnedColumn,
} from '../DataTable.model';
import {
  chunk,
  fromCellRef,
  hasDisplayedProperty,
  isChildColumn,
  isColumnExpanded,
  toCellRef,
} from '../DataTable.util';
import { DataTableCellStateService } from './DataTableCellStateService';
import { DataTableRect, DataTableRectForceLoad, DataTableSelection } from './DataTableSelection';
import { DataTableStateService } from './DataTableStateService';
import { DataTableValidator } from './DataTableValidator';
import { DataTableWorkflowService } from './DataTableWorkflowService';

/* eslint "no-unused-private-class-members": "warn" */

interface InternalColumnData {
  idToIndex: Map<DataTableColumnApiId, number>;
  indexToId: Map<number, DataTableColumnApiId>;
  columns: Map<DataTableColumnApiId, DataTableColumn>;
}

interface InternalRowData {
  idToIndex: Map<DataTableRowApiId, number>;
  indexToId: Map<number, DataTableRowApiId>;
  rows: Map<DataTableRowApiId, DataTableRow>;
}

interface CellSnapshot {
  readonly cells: ReadonlyArray<{
    readonly coordinate: Readonly<DataTableCellIdCoordinate>;
    readonly value: string | null;
    readonly updated_at: ISODateTime | null;
  }>;
}

/**
 * |-1-2-3----------------------|
 * |------|-(dueTime)-|---------|
 * |-------------------[1,2,3]--|
 * @param dueTime timeout duration in milliseconds
 */
const bufferedDebounce =
  <T>(dueTime: number) =>
  (source: Observable<T>): Observable<Array<T>> => {
    return source.pipe(buffer(source.pipe(debounceTime(dueTime))), filter(_isNotEmpty));
  };
const isColumnTypeReadonly = (type: DataTableColumnType): boolean => {
  switch (type) {
    case 'number':
    case 'timestamp':
    case 'timestampBaseline':
    case 'text':
    case 'measurement':
    case 'metadata':
      return false;
    default:
      return true;
  }
};

export class DataTableService implements IDataTableService {
  static #cellLoadAreaLimit = 350 * 350;
  static #initialLoadRect = new DataTableRect({ left: 0, right: 200, top: 0, bottom: 200 });
  static #MAX_UNDO_STACK_SIZE = 50;
  static #validSelection = (selection?: DataTableSelection): selection is DataTableSelection & boolean => {
    if (_notNil(selection)) {
      const columnCount = selection.bottomRight.column - selection.topLeft.column + 1;
      const rowCount = selection.bottomRight.row - selection.topLeft.row + 1;
      return columnCount * rowCount <= DataTableService.#cellLoadAreaLimit;
    }
    return false;
  };

  readonly #props: DataTableServiceProps;
  readonly #eventEmitter = new EventTarget();
  readonly #cellData = new Map<DataTableRowApiId, Map<DataTableColumnApiId, DataTableCellDataCache>>();
  readonly #cellAlertService = new DataTableCellAlertService();
  readonly #cellStateService = new DataTableCellStateService(this.#eventEmitter);
  readonly #tableStateService = new DataTableStateService(this.#eventEmitter);
  readonly #workflowService = new DataTableWorkflowService(this.#eventEmitter, this);

  #table?: DataTable;
  #columnData?: InternalColumnData;
  #rowData?: InternalRowData;
  #rowHeaders: Partial<Record<DataTableRowHeaderId, DataTableRowHeader>>;
  #columnGroupingData: DataTableColumnGroupingData | null = null;
  readonly #pinnedColumns: ReadonlyArray<PinnedColumn>;

  #selection?: IDataTableSelection;
  #undoStack: Array<CellSnapshot> = [];
  #redoStack: Array<CellSnapshot> = [];

  readonly #destroy$ = new Subject<void>();

  readonly #cellLoadRectOverride$ = new Subject<Readonly<DataTableRect>>();
  readonly #windowScrollRect$ = new BehaviorSubject<Readonly<DataTableRect> | null>(null);
  readonly #debouncedCellUpsert$ = new Subject<Array<DataTableCellUpsert>>();

  readonly #selectionRect$ = fromEvent<DataTableCellSelectionChangeEvent>(
    this.#eventEmitter,
    DataTableEvent.CellSelectionChange
  ).pipe(
    takeUntil(this.#destroy$),
    filter(
      ({
        detail: {
          source,
          selection: {
            rect: { area },
          },
        },
      }) => source === 'user' && area >= 10 && area <= DataTableService.#cellLoadAreaLimit
    ),
    map((selectionChangeEvent) => selectionChangeEvent.detail.selection.rect)
  );
  readonly #cellLoadRect$ = merge(
    this.#windowScrollRect$.pipe(filter((v) => _notNil(v))) as Observable<Readonly<DataTableRect>>,
    this.#selectionRect$,
    this.#cellLoadRectOverride$.pipe(filter((v) => _notNil(v)))
  ).pipe(
    takeUntil(this.#destroy$),
    distinctUntilChanged((previous: Readonly<DataTableRect>, current: Readonly<DataTableRect>) => {
      if ('force' in current && (current as DataTableRectForceLoad).force) {
        return false;
      }
      return previous.contains(current);
    }),
    filter(({ area }) => area <= DataTableService.#cellLoadAreaLimit),
    switchMap(({ top, bottom, left, right }) => {
      const from = this.fromIndexCoordinate({ column: left, row: top });
      const to = this.fromIndexCoordinate({ column: right, row: bottom });
      if (_notNil(from) && _notNil(to)) {
        return of({ from, to, include_alerts: true }).pipe(
          delay(500),
          this.props.apiService.loadCells(this),
          retry({
            count: 2,
            delay: (error, retryCount) => {
              if (
                axios.isAxiosError(error) &&
                (error.code === 'ERR_NETWORK' || (error.response?.status && error.response.status >= 500))
              ) {
                return timer(1000 * retryCount);
              }
              throw error;
            },
          }),
          catchError((err) => {
            ExceptionHandler.captureException(
              new InVivoError('Could not load cell window', {
                cause: err,
                slug: 'datatable-cells-load',
              })
            );

            if (notAborted(err)) {
              errorToast('Failed to load table cells. Please refresh to try again.');
            }

            return EMPTY;
          })
        );
      }
      return EMPTY;
    }),
    tap(() => this.validate()),
    observeOn(animationFrameScheduler),
    takeUntil(this.#destroy$),
    shareReplay(1)
  );

  // @ts-expect-error: ignore unused
  // eslint-disable-next-line no-unused-private-class-members
  readonly #cellLoadRectSubscription = this.#cellLoadRect$.subscribe(async (cellData) => {
    const { cellUpdateEvent, cellAlertUpdateEvent } = cellData.reduce<{
      cellUpdateEvent: DataTableCellUpdateEventDetail;
      cellAlertUpdateEvent: DataTableCellAlertUpdateEventDetail;
    }>(
      (acc, { column_id, row_id, cell: { value }, alerts, updated_at }) => {
        const coordinate: DataTableCellIdCoordinate = { column: column_id, row: row_id };
        if ('OK' !== this.cellStatus(coordinate).type) {
          return acc;
        }

        const ref = toCellRef(coordinate);
        acc.cellAlertUpdateEvent.cellAlerts[ref] = this.#cellAlertService.ingest(coordinate, alerts);
        if (this.#getCellData(coordinate)?.value !== value) {
          this.#setCellData(coordinate, { value, updated_at });
          acc.cellUpdateEvent[ref] = { value, updated_at };
        }
        return acc;
      },
      { cellUpdateEvent: {}, cellAlertUpdateEvent: { cellAlerts: {}, trigger: 'cell-load' } }
    );
    this.#eventEmitter.dispatchEvent(new CustomEvent(DataTableEvent.CellUpdate, { detail: cellUpdateEvent }));
    this.#eventEmitter.dispatchEvent(new CustomEvent(DataTableEvent.CellAlertUpdate, { detail: cellAlertUpdateEvent }));
  });

  // @ts-expect-error: ignore unused
  // eslint-enable no-unused-private-class-members
  #debouncedCellUpsertSubscription = this.#debouncedCellUpsert$ // eslint-disable-line no-unused-private-class-members
    .pipe(
      takeUntil(this.#destroy$),
      bufferedDebounce(500),
      mergeMap((buffer) => {
        const result = new Map(
          buffer.flat().map((cellUpsert) => [`${cellUpsert.column_id}${cellUpsert.row_id}`, cellUpsert])
        );

        return from(Promise.all<DataTableUpsertDetail>(this.handleChunkCellUpsert(2500, [...result.values()])));
      }),
      observeOn(animationFrameScheduler)
    )
    .subscribe((next) => {
      this.#handleUpdateCellsApiResponse(next);
    });

  readonly #handleKeydown = (event: KeyboardEvent): void => {
    if ((event.target as HTMLElement).tagName !== 'BODY') {
      return;
    }

    const handler = this.#getKeyHandler(event);
    if (_notNil(handler)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      handler();
    }
  };

  #getKeyHandler(event: KeyboardEvent): (() => void) | undefined {
    const { key, shiftKey } = event;

    if (key === 'Enter' && this.hasSelectedCells()) {
      return this.#getEnterHandler(shiftKey);
    }

    if (key === 'ArrowUp') {
      return () => this.moveSelection('UP', { append: shiftKey });
    }

    if (key === 'ArrowDown') {
      return () => this.moveSelection('DOWN', { append: shiftKey });
    }

    if (key === 'ArrowLeft') {
      return () => this.moveSelection('LEFT', { append: shiftKey });
    }

    if (key === 'ArrowRight') {
      return () => this.moveSelection('RIGHT', { append: shiftKey });
    }

    if (key === 'Tab') {
      return this.#getTabHandler(shiftKey);
    }

    if (key === 'Clear' || key === 'Backspace') {
      return () => this.clearSelection();
    }

    return this.#getSingleCharHandler(event);
  }

  #getEnterHandler(shiftKey: boolean): () => void {
    const shouldActivateWithDirection =
      _notNil(this.#selection) &&
      this.#selection.rect.area === 1 &&
      this.columnByIndex(this.#selection.topLeft?.column)?.type === 'timestamp';

    if (shouldActivateWithDirection) {
      return () => this.#activateSelection({ options: { direction: shiftKey ? 'UP' : 'DOWN' } });
    }

    return () => this.#activateSelection();
  }

  #getTabHandler(shiftKey: boolean): () => void {
    const shouldActivateWithDirection =
      this.workflowService.workflowActive &&
      _notNil(this.#selection) &&
      this.#selection.rect.area === 1 &&
      this.columnByIndex(this.#selection.topLeft?.column)?.type === 'timestamp';

    if (shouldActivateWithDirection) {
      return () => this.#activateSelection({ options: { direction: shiftKey ? 'LEFT' : 'RIGHT' } });
    }

    return () => this.moveSelection(shiftKey ? 'LEFT' : 'RIGHT');
  }

  #getSingleCharHandler(event: KeyboardEvent): (() => void) | undefined {
    const { key, metaKey, ctrlKey, shiftKey, location } = event;
    const isStandardLocation =
      location === KeyboardEvent.DOM_KEY_LOCATION_STANDARD || location === KeyboardEvent.DOM_KEY_LOCATION_NUMPAD;

    if (!isStandardLocation || key.length !== 1) {
      return undefined;
    }

    if (key === 'a' && (metaKey || ctrlKey)) {
      return () => this.#bulkSelectCells('all');
    }

    if (key === ' ' && ctrlKey) {
      return () => this.#bulkSelectCells('column');
    }

    if (key === ' ' && shiftKey) {
      return () => this.#bulkSelectCells('row');
    }

    if (key === 'z' && metaKey) {
      return shiftKey ? () => this.redo().then(() => this.validate()) : () => this.undo().then(() => this.validate());
    }

    if (this.hasSelectedCells() && !metaKey && !ctrlKey) {
      return () => this.#activateSelection({ content: key });
    }

    return undefined;
  }
  readonly #handleCopy = (event: ClipboardEvent) => {
    if ((event.target as HTMLElement).tagName !== 'INPUT') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#handleCutOrCopy('copy');
    }
  };

  readonly #handleCut = (event: ClipboardEvent) => {
    if ((event.target as HTMLElement).tagName !== 'INPUT') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#handleCutOrCopy('cut');
    }
  };
  readonly #handleAlertResolved = (event: AlertUpdateEvent) => {
    this.#eventEmitter.dispatchEvent(
      new CustomEvent(DataTableEvent.CellAlertUpdate, {
        detail: {
          trigger: 'cell-update',
          cellAlerts: this.#cellAlertService.resolve(event.detail),
        },
      })
    );
  };

  readonly #handleCutOrCopy = (type: 'cut' | 'copy') => {
    if (_notNil(this.#selection)) {
      const { topLeft, bottomRight } = this.#selection;
      const clipboardData: Array<string> = [];
      for (let row = topLeft.row; row <= bottomRight.row; row++) {
        const rowData: Array<string> = [];
        for (let column = topLeft.column; column <= bottomRight.column; column++) {
          const coordinate = this.fromIndexCoordinate({ column, row });
          rowData.push(_notNil(coordinate) ? (this.#getCellData(coordinate)?.value ?? '') : '');
        }
        clipboardData.push(rowData.join('\t'));
      }
      navigator.clipboard.writeText(clipboardData.join('\n'));
      if (type === 'cut') {
        this.clearSelection();
      }
    }
  };

  readonly #handlePaste = (event: ClipboardEvent) => {
    if (!this.#canPaste(event)) {
      return;
    }

    const pasteData = this.#parsePasteData(event.clipboardData);
    if (_isNil(pasteData)) {
      return;
    }

    if (pasteData.width * pasteData.height > 20000) {
      this.#eventEmitter.dispatchEvent(
        new CustomEvent<DataTablePasteLimitExceededEvent>(DataTableEvent.PasteLimitExceeded)
      );
      return;
    }

    const cellUpdates = this.#generatePasteCellUpdates(pasteData);
    if (_isNotEmpty(cellUpdates)) {
      this.#applyPasteUpdates(cellUpdates, pasteData);
    }
  };

  #canPaste(event: ClipboardEvent): boolean {
    return (
      event.clipboardData?.types?.includes('text/plain') === true &&
      _notNil(this.#selection) &&
      !this.props.readonly &&
      (event.target as HTMLElement).tagName !== 'INPUT'
    );
  }

  #parsePasteData(clipboardData: DataTransfer | null): {
    rows: Array<Array<string>>;
    width: number;
    height: number;
    repeatHorizontal: number;
    repeatVertical: number;
  } | null {
    const rowData =
      clipboardData
        ?.getData('text/plain')
        ?.split(/\r?\n/)
        .map((row) => row.split('\t')) ?? [];

    if (_isEmpty(rowData)) {
      return null;
    }

    const { topLeft, bottomRight } = this.#selection!;
    const selectionHeight = bottomRight.row - topLeft.row + 1;
    const selectionWidth = bottomRight.column - topLeft.column + 1;
    const pasteDataHeight = rowData.length;
    const pasteDataWidth = rowData[0]?.length ?? 0;

    const verticalRepeat = Math.max(1, Math.floor(selectionHeight / pasteDataHeight));
    const horizontalRepeat = Math.max(1, Math.floor(selectionWidth / pasteDataWidth));

    return {
      rows: rowData,
      width: pasteDataWidth,
      height: pasteDataHeight,
      repeatHorizontal: horizontalRepeat,
      repeatVertical: verticalRepeat,
    };
  }

  #generatePasteCellUpdates(pasteData: {
    rows: Array<Array<string>>;
    width: number;
    height: number;
    repeatHorizontal: number;
    repeatVertical: number;
  }): Array<DataTableCellUpsert> {
    const { topLeft } = this.#selection!;
    const { rows, width, height, repeatHorizontal, repeatVertical } = pasteData;

    const cellUpdates: Array<DataTableCellUpsert> = [];

    for (let rowRepeat = 0; rowRepeat < repeatVertical; rowRepeat++) {
      const rowOffset = height * rowRepeat;

      for (let pasteDataRowIndex = 0; pasteDataRowIndex < rows.length; pasteDataRowIndex++) {
        const pasteRow = rows[pasteDataRowIndex];

        for (let columnRepeat = 0; columnRepeat < repeatHorizontal; columnRepeat++) {
          const columnOffset = width * columnRepeat;

          for (let pasteDataColumnIndex = 0; pasteDataColumnIndex < pasteRow.length; pasteDataColumnIndex++) {
            const value = pasteRow[pasteDataColumnIndex];

            if (_isNotBlank(value)) {
              const column = pasteDataColumnIndex + columnOffset + topLeft.column;
              const row = pasteDataRowIndex + rowOffset + topLeft.row;
              const coord = this.fromIndexCoordinate({ column, row });

              if (_notNil(coord)) {
                cellUpdates.push({ column_id: coord.column, row_id: coord.row, value });
              }
            }
          }
        }
      }
    }

    return cellUpdates;
  }

  #applyPasteUpdates(
    cellUpdates: Array<DataTableCellUpsert>,
    pasteData: { width: number; height: number; repeatHorizontal: number; repeatVertical: number }
  ): void {
    const { topLeft } = this.#selection!;
    const { width, height, repeatHorizontal, repeatVertical } = pasteData;

    this.updateCells(cellUpdates).then(() => this.validate());

    this.#selection = new DataTableSelection({
      from: topLeft,
      to: {
        column: topLeft.column + repeatHorizontal * width - 1,
        row: topLeft.row + repeatVertical * height - 1,
      },
    });

    this.#dispatchSelectionChange({
      source: 'api',
      scrollTo: false,
      activate: false,
    });
  }

  constructor(private readonly props: DataTableServiceProps) {
    this.#props = Object.freeze(props);
    this.#rowHeaders = structuredCloneUtil(props.rowHeaders).reduce((acc, rh) => ({ ...acc, [rh.id]: rh }), {});
    this.#pinnedColumns = props.pinnedColumns ?? [];
    document.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('paste', this.#handlePaste);
    document.addEventListener('copy', this.#handleCopy);
    document.addEventListener('cut', this.#handleCut);
    AlertService.addEventListener('alert-resolved', this.#handleAlertResolved, { passive: true });
  }

  /**
   * Helper method to get cell data from nested Map structure.
   * O(1) access without string template creation.
   */
  #getCellData(coordinate: DataTableCellIdCoordinate): DataTableCellDataCache | undefined {
    return this.#cellData.get(coordinate.row)?.get(coordinate.column);
  }

  /**
   * Helper method to set cell data in nested Map structure.
   * O(1) access without string template creation.
   */
  #setCellData(coordinate: DataTableCellIdCoordinate, data: DataTableCellDataCache): void {
    let rowMap = this.#cellData.get(coordinate.row);
    if (_isNil(rowMap)) {
      rowMap = new Map();
      this.#cellData.set(coordinate.row, rowMap);
    }
    rowMap.set(coordinate.column, data);
  }

  /**
   * Helper method to check if cell data exists in nested Map structure.
   * O(1) access without string template creation.
   */
  #hasCellData(coordinate: DataTableCellIdCoordinate): boolean {
    return this.#cellData.get(coordinate.row)?.has(coordinate.column) ?? false;
  }

  /**
   * Updates internal column data structures after column changes.
   * Processes column grouping and regenerates internal column maps.
   *
   * @param columns - Array of columns to process
   */
  #updateColumnData(columns: Array<DataTableColumn>): void {
    this.#columnGroupingData = this.#processColumnsForGrouping(columns);
    const columnsForData = this.#columnGroupingData?.leafColumns ?? columns;
    this.#columnData = DataTableService.#generateInternalColumnData(columnsForData);
  }

  get name(): Readonly<string | undefined> {
    return Object.freeze(this.#table?.name);
  }

  get tableId(): Readonly<DataTableApiId | undefined> {
    return Object.freeze(this.#table?.id as DataTableApiId | undefined);
  }

  get studyApiId(): Readonly<StudyApiId> {
    return Object.freeze(this.#props.studyApiId);
  }

  get dimensions(): Readonly<DataTableDimensions> {
    return Object.freeze({ ...this.#props.dimensions });
  }

  get type(): Readonly<DataTableType | 'unknown'> {
    return Object.freeze(this.#table?.type ?? 'unknown');
  }

  get unit(): Readonly<string | null | undefined> {
    return Object.freeze(this.#table?.unit);
  }

  get readonly(): Readonly<boolean> {
    return Object.freeze(this.#props.readonly);
  }

  get columns(): Readonly<Array<DataTableColumn>> {
    // Return ALL columns including parents. Use leafColumns for renderable columns only.
    return Object.freeze((this.#table?.columns ?? []) as Array<DataTableColumn>);
  }

  get leafColumns(): Readonly<Array<DataTableColumn>> {
    return Object.freeze(this.#columnGroupingData?.leafColumns ?? this.columns);
  }

  get rows(): Readonly<Array<DataTableRow>> {
    return Object.freeze([...(this.#rowData?.rows?.values() ?? [])]);
  }

  get columnCount(): number {
    return this.#columnData?.columns?.size ?? 0;
  }

  get rowCount(): number {
    return this.#rowData?.rows?.size ?? 0;
  }

  get selection(): Readonly<DataTableSelection | undefined> {
    return Object.freeze(this.#selection);
  }

  get working(): Readonly<boolean> {
    return this.#tableStateService.working;
  }

  get workflowService(): Readonly<DataTableWorkflowService> {
    return this.#workflowService;
  }

  get featureFlags(): Readonly<DataTableFeatureFlags> {
    return Object.freeze({ ...(this.#props.featureFlags ?? {}) });
  }

  async initialise(): Promise<DataTableService> {
    this.#table = await this.#invokeDataTableFetchAPI();

    // Use API settings if available, otherwise use initial config from props
    const displayColumns = this.#table?.settings?.display_columns ?? [];
    const displayedRowHeaderIds = _isNotEmpty(displayColumns)
      ? new Set(displayColumns as Array<DataTableRowHeaderId>)
      : new Set(
          Object.values(this.#rowHeaders)
            .filter((rh) => rh.displayed)
            .map((rh) => rh.id)
        );

    await this.updateDisplayedRowHeaders(displayedRowHeaderIds, { persist: false });

    this.#columnGroupingData = this.#processColumnsForGrouping((this.#table?.columns ?? []) as Array<DataTableColumn>);

    // Use leaf columns for internal data if grouping is enabled
    const columnsForData =
      this.#columnGroupingData?.leafColumns ?? ((this.#table?.columns ?? []) as Array<DataTableColumn>);
    this.#columnData = DataTableService.#generateInternalColumnData(columnsForData);

    this.#rowData = DataTableService.#generateInternalRowData((this.#table?.rows ?? []) as Array<DataTableRow>);

    const columnCount = this.columnCount;
    const rowCount = this.rowCount;
    const { right: initialRight, bottom: initialBottom } = DataTableService.#initialLoadRect;
    const right = (columnCount > initialRight ? initialRight : columnCount) - 1;
    const bottom = (rowCount > initialBottom ? initialBottom : rowCount) - 1;
    this.#workflowService.initialise();
    this.#cellLoadRectOverride$.next(new DataTableRect({ left: 0, right, top: 0, bottom }));

    const $cellLoadRectInitialValue = firstValueFrom(this.#cellLoadRect$.pipe(takeUntil(this.#destroy$))).catch(
      (err) => {
        if (!(err instanceof EmptyError)) {
          throw new InVivoError('Datatable cell window load error', {
            cause: err,
            slug: 'datatable-cell-window-load',
          });
        }
      }
    );
    // if the datatable has columns await the response
    if (this.rowCount > 0 && this.columnCount > 0) {
      await $cellLoadRectInitialValue;
    }
    return this;
  }

  async validate(): Promise<void> {
    await this.#invokeDataTableValidateApi({} as DataTableWindowClearRequest).then((validation) => {
      this.#cellStateService.clearType('AsyncValidationWarning');
      this.#cellStateService.asyncValidationErrror(validation.errors, []);
    });
  }

  validateColumn(...columns: Array<DataTableColumnApiId>): void {
    const invalidCells = columns.reduce<Array<DataTableCellUpsertError>>((columnAcc, columnId) => {
      const column = this.columnById(columnId);
      if (_notNil(column)) {
        this.rows.reduce<Array<DataTableCellUpsertError>>((rowAcc, row) => {
          const cell = this.cellData({ column: column.id, row: row.id });
          const validationResult = DataTableValidator.validate(column?.type, cell?.value ?? null);
          if (!validationResult.valid) {
            rowAcc.push({
              column_id: column.id,
              row_id: row.id,
              value: cell?.value ?? null,
              error: validationResult.error,
            });
          }
          return rowAcc;
        }, columnAcc);
      }
      return columnAcc;
    }, []);
    this.#cellStateService.validationError(invalidCells);
  }

  destroy() {
    this.#destroy$.next();
    this.#destroy$.complete();
    this.#cellStateService.destroy();
    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('paste', this.#handlePaste);
    document.removeEventListener('copy', this.#handleCopy);
    document.removeEventListener('cut', this.#handleCut);
    AlertService.removeEventListener('alert-resolved', this.#handleAlertResolved);

    this.#cellData.clear();
    this.#columnData?.columns?.clear();
    this.#columnData?.idToIndex?.clear();
    this.#columnData?.indexToId?.clear();
    this.#rowData?.rows?.clear();
    this.#rowData?.idToIndex?.clear();
    this.#rowData?.indexToId?.clear();
    this.#selection = undefined;
    this.#undoStack = [];
    this.#redoStack = [];
  }

  hasColumn(columnId: DataTableColumnApiId): boolean {
    return this.#columnData?.columns?.has(columnId) ?? false;
  }

  cellData(cellCoordinate: DataTableCellIdCoordinate | undefined): DataTableCellDataCache | undefined {
    if (_notNil(cellCoordinate)) {
      // Direct validation using ID lookup maps (eliminates wasteful coordinate conversion)
      const columnExists = this.#columnData?.columns.has(cellCoordinate.column) ?? false;
      const rowExists = this.#rowData?.rows.has(cellCoordinate.row) ?? false;

      if (columnExists && rowExists) {
        return this.#getCellData(cellCoordinate);
      }
    }
  }

  cellAlerts(cellCoordinate: DataTableCellIdCoordinate | undefined): DataTableCellAlertCache | undefined {
    if (_notNil(cellCoordinate)) {
      // Direct validation using ID lookup maps (eliminates wasteful coordinate conversion)
      const columnExists = this.#columnData?.columns.has(cellCoordinate.column) ?? false;
      const rowExists = this.#rowData?.rows.has(cellCoordinate.row) ?? false;

      if (columnExists && rowExists) {
        return this.#cellAlertService.get(cellCoordinate);
      }
    }
  }

  windowScrollChange(rect: DataTableRect): void {
    this.#windowScrollRect$.next(Object.freeze(rect));
  }

  async #invokeDataTableFetchAPI(): Promise<DataTable> {
    return firstValueFrom(of(null).pipe(takeUntil(this.#destroy$), this.#props.apiService.loadTable(this)));
  }

  async #invokeCellUpdateApi(updates: Array<DataTableCellUpsert>): Promise<DataTableCellUpsertResponse> {
    if (_isEmpty(updates)) {
      return Promise.resolve({ success: true, effects: [] });
    }
    return firstValueFrom(of(updates).pipe(takeUntil(this.#destroy$), this.#props.apiService.updateCells(this)));
  }

  async #invokeDataTableValidateApi(
    windowClearRequest: DataTableWindowClearRequest
  ): Promise<DataTableValidateResponse> {
    return firstValueFrom(
      of(windowClearRequest).pipe(takeUntil(this.#destroy$), this.#props.apiService.validate(this))
    );
  }

  async #invokeWindowClearApi(windowClearRequest: DataTableWindowClearRequest): Promise<DataTableWindowClearResponse> {
    return firstValueFrom(
      of(windowClearRequest).pipe(takeUntil(this.#destroy$), this.#props.apiService.clearWindow(this))
    );
  }

  async #invokeDataTableSettingsUpdateApi(settingsUpdate: DataTableSettingsUpdate): Promise<void> {
    return firstValueFrom(
      of(settingsUpdate).pipe(takeUntil(this.#destroy$), this.#props.apiService.updateTableSettings(this))
    );
  }

  async #invokeDataTableColumMoveApi(
    columnMoveRequest: DataTableColumnMoveRequest
  ): Promise<DataTableMoveColumnResponse> {
    return firstValueFrom(
      of(columnMoveRequest).pipe(takeUntil(this.#destroy$), this.#props.apiService.moveColumn(this))
    );
  }

  async #invokeDataTableColumAddApi(columnAddRequest: DataTableColumnAddRequest): Promise<Array<DataTableColumn>> {
    return firstValueFrom(of(columnAddRequest).pipe(takeUntil(this.#destroy$), this.#props.apiService.addColumn(this)));
  }

  async #invokeDataTableColumnUpdateApi(columnUpdateRequest: DataTableColumnUpdateRequest): Promise<DataTableColumn> {
    return firstValueFrom(
      of(columnUpdateRequest).pipe(takeUntil(this.#destroy$), this.#props.apiService.updateColumn(this))
    );
  }

  async #invokeDataTableRowAddApi(rowAddRequest: DataTableRowAddRequest): Promise<Array<DataTableRow>> {
    return firstValueFrom(of(rowAddRequest).pipe(takeUntil(this.#destroy$), this.#props.apiService.addRow(this)));
  }

  async #invokeDataTableColumRemoveApi(columnId: DataTableColumnApiId): Promise<DataTableColumnRemoveResponse> {
    return firstValueFrom(of(columnId).pipe(takeUntil(this.#destroy$), this.#props.apiService.removeColumn(this)));
  }

  async #invokeDataTableRowRemoveApi(rowId: DataTableRowApiId): Promise<void> {
    return firstValueFrom(of(rowId).pipe(takeUntil(this.#destroy$), this.#props.apiService.removeRow(this)));
  }

  async #invokeDataTableColumFillApi(
    columnId: DataTableColumnApiId,
    fillData: DataTableColumnFillRequestV1
  ): Promise<DataTableCellUpsertResponse> {
    return firstValueFrom(
      of({ column_id: columnId, request: fillData }).pipe(
        takeUntil(this.#destroy$),
        this.#props.apiService.fillColumn(this)
      )
    );
  }

  async #invokeDataTableColumnExpansionApi(
    request: DataTableColumnExpansionRequest
  ): Promise<DataTableColumnExpansionResponseAffectedColumns> {
    return firstValueFrom(
      of(request.columns).pipe(takeUntil(this.#destroy$), this.#props.apiService.updateColumnExpansion(this))
    );
  }

  handleChunkCellUpsert(
    chunkSize = 2500 as number,
    validCells: Array<DataTableCellUpsert>
  ): Array<Promise<DataTableUpsertDetail>> {
    const validCellChunks = chunk(validCells, { chunkSize });
    let eventDetail: DataTableHighVolumeUpsertDetail = {
      totalChunkedRequests: validCellChunks.length,
      pending: [...validCellChunks],
      resolved: [],
      rejected: [],
    };
    if (validCellChunks.length > 1) {
      this.#eventEmitter.dispatchEvent(
        new CustomEvent(DataTableEvent.HighVolumeUpsert, {
          detail: eventDetail,
        })
      );
    }
    return validCellChunks.map((chunk, chunkIndex) =>
      this.#tableStateService.wrapOperation(
        this.#invokeCellUpdateApi(chunk)
          .then((response) => {
            if (validCellChunks.length > 1) {
              eventDetail = {
                ...eventDetail,
                totalChunkedRequests: eventDetail.totalChunkedRequests--,
                pending: eventDetail.pending.splice(chunkIndex, 0),
                resolved: [...eventDetail.resolved, chunk],
              };
              this.#eventEmitter.dispatchEvent(
                new CustomEvent(DataTableEvent.HighVolumeUpsert, {
                  detail: eventDetail,
                })
              );
            }
            return {
              cells: chunk,
              invalidCells: response.success ? [] : [...response.errors],
              networkErrors: [],
              effects: response.effects ?? [],
              alerts: response.data_table_alerts ?? [],
            };
          })
          .catch((err) => {
            ExceptionHandler.captureException(
              new InVivoError('Failed to upsert cell batch', {
                cause: err,
                slug: 'datatable-cell-upsert-error',
                context: {
                  chunkSize: `${chunk.length}`,
                  chunkIndex: `${chunkIndex}`,
                },
              })
            );

            if (validCellChunks.length > 1) {
              eventDetail = {
                ...eventDetail,
                pending: eventDetail.pending.splice(chunkIndex, 0),
                rejected: [...eventDetail.rejected, chunk],
              };
              this.#eventEmitter.dispatchEvent(
                new CustomEvent(DataTableEvent.HighVolumeUpsert, {
                  detail: eventDetail,
                })
              );
            }
            return { cells: chunk, invalidCells: [], networkErrors: chunk, effects: [], alerts: [] };
          })
      )
    );
  }

  #handleUndoRedoStackUpdate(
    snapshotType: Required<DataTableCellsUpdateOptions>['snapshot'],
    operations: Array<[DataTableCellReference, DataTableCellDataCache]>
  ): void {
    const snapshot: CellSnapshot = {
      cells: operations.map(([cellRef, data]) => {
        const coordinate = fromCellRef(cellRef);
        if (_isNil(coordinate)) {
          throw new InVivoError('Invalid cell reference in undo/redo snapshot', { slug: 'invalid-cell-ref' });
        }
        return Object.freeze({
          coordinate: Object.freeze(coordinate),
          value: data.value,
          updated_at: data.updated_at,
        });
      }),
    };

    let emit = false;
    switch (snapshotType) {
      case 'normal':
        emit = this.#undoStack.length === 0 || this.redoEnabled();
        this.#undoStack.push(snapshot);
        this.#redoStack = [];
        break;
      case 'redo':
        emit = !this.undoEnabled() || !this.redoEnabled();
        this.#undoStack.push(snapshot);
        break;
      case 'undo':
        emit = !this.redoEnabled() || !this.undoEnabled();
        this.#redoStack.push(snapshot);
        break;
    }

    // Enforce stack size limit
    if (this.#undoStack.length > DataTableService.#MAX_UNDO_STACK_SIZE) {
      this.#undoStack.shift();
    }

    if (emit) {
      this.#eventEmitter.dispatchEvent(
        new CustomEvent(DataTableEvent.UndoRedoStateChange, {
          detail: { redoEnabled: this.redoEnabled(), undoEnabled: this.undoEnabled() },
        })
      );
    }
  }

  #handleUpdateCellsApiResponse(
    result: Array<DataTableUpsertDetail>,
    options: { invalidCells?: Array<DataTableCellUpsertError>; updated_at?: ISODateTime } = {}
  ): void {
    const { cells, invalidCells, networkErrors, effects, alerts } = result.reduce<DataTableUpsertDetail>(
      (acc, resultChunk) => {
        acc.cells.push(...resultChunk.cells);
        acc.invalidCells.push(...resultChunk.invalidCells);
        acc.networkErrors.push(...resultChunk.networkErrors);
        acc.effects.push(...resultChunk.effects);
        acc.alerts.push(...resultChunk.alerts);
        return acc;
      },
      { cells: [], invalidCells: options?.invalidCells ?? [], networkErrors: [], effects: [], alerts: [] }
    );
    this.#handleSideEffects(
      effects,
      cells.map((c) => c.row_id)
    );
    this.#cellStateService.validationError(invalidCells, cells);
    this.#cellStateService.networkError(networkErrors);
    this.#eventEmitter.dispatchEvent(
      new CustomEvent(DataTableEvent.CellAlertUpdate, {
        detail: alerts.reduce<DataTableCellAlertUpdateEventDetail>(
          (acc, cellAlert) => {
            const ref = toCellRef({ column: cellAlert.column_id, row: cellAlert.row_id });
            acc.cellAlerts[ref] = this.#cellAlertService.ingest(
              {
                column: cellAlert.column_id,
                row: cellAlert.row_id,
              },
              cellAlert.alerts
            );
            return acc;
          },
          { cellAlerts: {}, trigger: 'cell-update' }
        ),
      })
    );
  }

  async updateCells(
    cellUpdates: Array<DataTableCellUpsert>,
    options?: DataTableCellsUpdateOptions
  ): Promise<DataTableCellUpdateEventDetail> {
    this.#ensureNotReadonly();
    const snapshotType = options?.snapshot ?? 'normal';
    const snapshotEnabled = snapshotType !== 'none';
    const updated_at = DateUtils.dateTimeNow();
    const {
      validCells,
      validCells$,
      invalidCells: invalidCellsBeforePersist,
      updateEvent,
      cellAlertUpdateEvent,
      undoOrRedoOperations,
    } = cellUpdates.reduce<{
      validCells: Array<DataTableCellUpsert>;
      validCells$: Array<DataTableCellUpsert>;
      invalidCells: Array<DataTableCellUpsertError>;
      undoOrRedoOperations: Array<[DataTableCellReference, DataTableCellDataCache]>;
      updateEvent: DataTableCellUpdateEventDetail;
      cellAlertUpdateEvent: DataTableCellAlertUpdateEventDetail;
    }>(
      (acc, cellUpdate) => {
        const column = this.columnById(cellUpdate.column_id);
        const coordinate: DataTableCellIdCoordinate = { column: cellUpdate.column_id, row: cellUpdate.row_id };
        const ref = toCellRef(coordinate);
        const previous = this.#getCellData(coordinate);
        const validationResult = DataTableValidator.validate(column?.type, cellUpdate.value);
        if (!this.#isColumnReadonly(column)) {
          acc.updateEvent[ref] = { value: cellUpdate.value, updated_at };
          this.#setCellData(coordinate, { value: cellUpdate.value, updated_at });
          if (snapshotEnabled && previous?.value !== cellUpdate.value) {
            acc.undoOrRedoOperations.push([
              ref,
              {
                value: previous?.value ?? null,
                updated_at: previous?.updated_at ?? '0',
              },
            ]);
          }
          if (validationResult.valid) {
            if (previous?.value !== cellUpdate.value) {
              acc.cellAlertUpdateEvent.cellAlerts[ref] = {
                column: cellUpdate.column_id,
                row: cellUpdate.row_id,
                alerts: new Map<ID, AnimalAlertV1>(),
              };
              this.#cellAlertService.delete({ column: cellUpdate.column_id, row: cellUpdate.row_id });
              if (column?.type === 'measurement' && _isNil(options?.windowClear)) {
                acc.validCells$.push(cellUpdate);
              } else {
                acc.validCells.push(cellUpdate);
              }
            }
          } else {
            acc.invalidCells.push({ ...cellUpdate, error: validationResult.error });
          }
        }
        return acc;
      },
      {
        validCells: [],
        validCells$: [],
        invalidCells: [],
        undoOrRedoOperations: [],
        updateEvent: {},
        cellAlertUpdateEvent: { cellAlerts: {}, trigger: 'cell-load' },
      }
    );
    this.#eventEmitter.dispatchEvent(new CustomEvent(DataTableEvent.CellUpdate, { detail: updateEvent }));
    this.#eventEmitter.dispatchEvent(new CustomEvent(DataTableEvent.CellAlertUpdate, { detail: cellAlertUpdateEvent }));
    if (snapshotEnabled && _isNotEmpty(undoOrRedoOperations)) {
      this.#handleUndoRedoStackUpdate(snapshotType, undoOrRedoOperations);
    }
    if (_isNotEmpty(validCells$)) {
      this.#debouncedCellUpsert$.next(validCells$);
    }

    return Promise.all<DataTableUpsertDetail>(
      _isNil(options?.windowClear)
        ? this.handleChunkCellUpsert(2500, validCells)
        : new Array(validCells).map((chunk) =>
            this.#tableStateService.wrapOperation(
              this.#invokeWindowClearApi({ ...(options?.windowClear as DataTableWindowClearRequest) })
                .then((response) => ({
                  cells: chunk,
                  invalidCells: [],
                  networkErrors: [],
                  effects: response.effects ?? [],
                  alerts: [],
                }))
                .catch((err) => {
                  ExceptionHandler.captureException(
                    new InVivoError('Failed to clear cell batch', {
                      cause: err,
                      slug: 'datatable-cell-clear-error',
                      context: {
                        chunkSize: `${chunk.length}`,
                      },
                    })
                  );
                  return { cells: chunk, invalidCells: [], networkErrors: chunk, effects: [], alerts: [] };
                })
            )
          )
    ).then((result) => {
      this.#handleUpdateCellsApiResponse(result, { invalidCells: invalidCellsBeforePersist, updated_at });
      return updateEvent;
    });
  }

  cellSelected(cellCoordinate: DataTableCellIndexCoordinate): boolean {
    return this.#selection?.contains(cellCoordinate) ?? false;
  }

  columnSelected(column: number): boolean {
    return this.#selection?.containsColumn(column) ?? false;
  }

  rowSelected(row: number): boolean {
    return this.#selection?.containsRow(row) ?? false;
  }

  cellState(cellCoordinate: DataTableCellIdCoordinate | undefined): DataTableCellState {
    if (_notNil(cellCoordinate)) {
      return this.#cellStateService.cellState(cellCoordinate, this.#hasCellData(cellCoordinate));
    }
    return 'Unknown';
  }

  cellStatus(cellCoordinate: DataTableCellIdCoordinate | undefined): DataTableCellStatus {
    if (_notNil(cellCoordinate)) {
      return this.#cellStateService.cellStatus(cellCoordinate);
    }
    return { type: 'OK' };
  }

  rowHasError(row: DataTableRowApiId | undefined): boolean {
    return this.#cellStateService.rowInvalid(row);
  }

  columnByIndex(index: number | undefined): DataTableColumn | undefined {
    if (_notNil(index) && Number.isFinite(index)) {
      return this.#columnData?.columns?.get(this.#columnData?.indexToId?.get(index) ?? 'dtc_empty');
    }
  }

  columnById(columnId: DataTableColumnApiId | undefined): DataTableColumn | undefined {
    if (_notNil(columnId)) {
      return this.#columnData?.columns?.get(columnId);
    }
  }

  columnsByIds(columnIds: Array<DataTableColumnApiId> | undefined): Array<DataTableColumn> {
    if (_notNil(columnIds)) {
      return columnIds.reduce((acc, colId) => {
        const col = this.columnById(colId);
        if (_notNil(col)) {
          acc.push(col);
        }
        return acc;
      }, [] as Array<DataTableColumn>);
    }
    return [];
  }

  rowByIndex(index: number | undefined): DataTableRow | undefined {
    if (_notNil(index) && Number.isFinite(index)) {
      return this.#rowData?.rows?.get(this.#rowData?.indexToId?.get(index) ?? 'dtr_empty');
    }
  }

  fromIndexCoordinate(cellCoordinate?: DataTableCellIndexCoordinate): DataTableCellIdCoordinate | undefined {
    if (_notNil(cellCoordinate)) {
      const column = this.#columnData?.indexToId?.get(cellCoordinate.column);
      const row = this.#rowData?.indexToId?.get(cellCoordinate.row);
      if (_notNil(column) && _notNil(row)) {
        return { column, row };
      }
    }
  }

  moveSelection(direction: DataTableDirection, options?: DataTableMoveSelectionOptions): void {
    if (_isNil(this.#selection)) {
      this.selectCell({ column: 0, row: 0 }, { scrollTo: false });
      return;
    }

    const append = options?.append ?? false;
    const startCoordinate = append ? this.#selection.to : this.#selection.from;
    const nextCoordinate = this.#calculateNextCoordinate(startCoordinate, direction);
    const updatedSelection = append
      ? new DataTableSelection({ from: this.#selection.from, to: nextCoordinate })
      : new DataTableSelection({ from: nextCoordinate, to: nextCoordinate });

    if (DataTableService.#validSelection(updatedSelection)) {
      this.#selection = updatedSelection;
      this.#dispatchSelectionChange({
        source: 'user',
        scrollTo: false,
        activate: false,
      });
    }
  }

  #calculateNextCoordinate(
    coordinate: DataTableCellIndexCoordinate,
    direction: DataTableDirection
  ): DataTableCellIndexCoordinate {
    let { column, row } = coordinate;

    switch (direction) {
      case 'UP':
        row--;
        break;
      case 'DOWN':
        row++;
        break;
      case 'LEFT':
        column--;
        break;
      case 'RIGHT':
        column++;
        break;
    }

    const maxColumnIndex = this.columnCount - 1;
    const maxRowIndex = this.rowCount - 1;

    return {
      column: Math.max(0, Math.min(column, maxColumnIndex)),
      row: Math.max(0, Math.min(row, maxRowIndex)),
    };
  }

  selectRow(rowIndex: number): void {
    const updatedSelection = new DataTableSelection({
      from: { column: 0, row: rowIndex },
      to: { column: this.columnCount - 1, row: rowIndex },
    });
    if (DataTableService.#validSelection(updatedSelection)) {
      this.#selection = updatedSelection;
      this.#dispatchSelectionChange({
        source: 'user',
        scrollTo: false,
        activate: false,
      });
    }
  }

  selectColumn(columnIndex: number): void {
    const updatedSelection = new DataTableSelection({
      from: { column: columnIndex, row: 0 },
      to: { column: columnIndex, row: this.rowCount - 1 },
    });
    if (DataTableService.#validSelection(updatedSelection)) {
      this.#selection = updatedSelection;
      this.#dispatchSelectionChange({
        source: 'user',
        scrollTo: false,
        activate: false,
      });
    }
  }

  #bulkSelectCells(operation: 'all' | 'column' | 'row'): void {
    let updatedSelection;
    switch (operation) {
      case 'all': {
        updatedSelection = new DataTableSelection({
          from: { column: 0, row: 0 },
          to: { column: this.columnCount - 1, row: this.rowCount - 1 },
        });
        break;
      }
      case 'column': {
        if (_notNil(this.#selection)) {
          updatedSelection = new DataTableSelection({
            from: { column: this.#selection.topLeft.column, row: 0 },
            to: { column: this.#selection.bottomRight.column, row: this.rowCount - 1 },
          });
        }
        break;
      }
      case 'row': {
        if (_notNil(this.#selection)) {
          updatedSelection = new DataTableSelection({
            from: { column: 0, row: this.#selection.topLeft.row },
            to: { column: this.columnCount - 1, row: this.#selection.bottomRight.row },
          });
        }
        break;
      }
    }
    if (DataTableService.#validSelection(updatedSelection)) {
      this.#selection = updatedSelection;
      this.#dispatchSelectionChange({
        source: 'user',
        scrollTo: false,
        activate: false,
      });
    }
  }

  selectCell(cellCoordinate: DataTableCellIndexCoordinate, options: DataTableCellSelectionOptions): void {
    let updatedSelection;
    if (options?.append ?? false) {
      updatedSelection = new DataTableSelection({
        from: this.#selection?.from ?? { column: 0, row: 0 },
        to: cellCoordinate,
      });
    } else {
      updatedSelection = new DataTableSelection({ from: cellCoordinate, to: cellCoordinate });
    }

    if (DataTableService.#validSelection(updatedSelection)) {
      this.#selection = updatedSelection;
      this.#dispatchSelectionChange({
        source: options?.source ?? 'user',
        scrollTo: options?.scrollTo ?? false,
        activate: options?.activate ?? false,
      });
    }
  }

  hasSelectedCells(): boolean {
    return _notNil(this.#selection);
  }

  subscribe<Event extends DataTableEvent>(
    event: Event,
    listener: DataTableListener[Event],
    signal?: AbortSignal
  ): void {
    this.#eventEmitter.addEventListener(event, listener as EventListener, { passive: true, signal });
  }

  unsubscribe<Event extends DataTableEvent>(event: Event, listener: DataTableListener[Event]): void {
    this.#eventEmitter.removeEventListener(event, listener as EventListener);
  }

  toIndexCoordinate(cellCoordinate?: DataTableCellIdCoordinate): DataTableCellIndexCoordinate | undefined {
    if (_notNil(cellCoordinate)) {
      const column = this.#columnData?.idToIndex?.get(cellCoordinate.column);
      const row = this.#rowData?.idToIndex?.get(cellCoordinate.row);
      if (_notNil(column) && _notNil(row)) {
        return { column, row };
      }
    }
  }

  toIndexColumn(columnId?: DataTableColumnApiId): DataTableColumnIndex | undefined {
    if (_notNil(columnId)) {
      return this.#columnData?.idToIndex.get(columnId);
    }
  }

  toIndexRow(rowId?: DataTableRowApiId): DataTableRowIndex | undefined {
    if (_notNil(rowId)) {
      return this.#rowData?.idToIndex.get(rowId);
    }
  }

  async undo(): Promise<DataTableCellUpdateEventDetail> {
    if (this.#undoStack.length > 0) {
      const snapshot = this.#undoStack.pop();
      if (_isNil(snapshot)) {
        return Promise.reject(new InVivoError('Nothing to undo', { slug: 'datatable-undo-empty' }));
      }

      const updates = snapshot.cells.map(({ coordinate, value }) => ({
        column_id: coordinate.column,
        row_id: coordinate.row,
        value,
      }));

      const updateCellsPromise = this.updateCells(updates, { snapshot: 'undo' });
      await updateCellsPromise;
      this.validate();
      return updateCellsPromise;
    }
    return Promise.reject(new InVivoError('Nothing to undo', { slug: 'datatable-undo-empty' }));
  }

  rowHeader(id: DataTableRowApiId): DataTableRow | undefined {
    return this.#rowData?.rows?.get(id);
  }

  async redo(): Promise<DataTableCellUpdateEventDetail> {
    if (this.#redoStack.length > 0) {
      const snapshot = this.#redoStack.pop();
      if (_isNil(snapshot)) {
        return Promise.reject(new InVivoError('Nothing to redo', { slug: 'datatable-redo-empty' }));
      }

      const updates = snapshot.cells.map(({ coordinate, value }) => ({
        column_id: coordinate.column,
        row_id: coordinate.row,
        value,
      }));

      const updateCellsPromise = this.updateCells(updates, { snapshot: 'redo' });
      await updateCellsPromise;
      this.validate();

      return updateCellsPromise;
    }
    return Promise.reject(new InVivoError('Nothing to redo', { slug: 'datatable-redo-empty' }));
  }

  async clearSelection(): Promise<void> {
    if (_isNil(this.#selection) || this.#props.readonly) {
      return;
    }

    const cellUpdates = this.#generateClearSelectionUpdates();
    const windowClearOptions = this.#buildWindowClearOptions();

    if (_notNil(windowClearOptions)) {
      await this.updateCells(cellUpdates, { windowClear: windowClearOptions });
    } else {
      await this.updateCells(cellUpdates);
    }

    this.validate();
  }

  #generateClearSelectionUpdates(): Array<DataTableCellUpsert & { snapshot: boolean }> {
    const { topLeft, bottomRight } = this.#selection!;
    const cellUpdates: Array<DataTableCellUpsert & { snapshot: boolean }> = [];

    for (let column = topLeft.column; column <= bottomRight.column; column++) {
      for (let row = topLeft.row; row <= bottomRight.row; row++) {
        const idCoordinate = this.fromIndexCoordinate({ column, row });
        if (_notNil(idCoordinate)) {
          cellUpdates.push({
            column_id: idCoordinate.column,
            row_id: idCoordinate.row,
            snapshot: true,
            value: null,
          });
        }
      }
    }

    return cellUpdates;
  }

  #buildWindowClearOptions(): DataTableWindowClearRequest | null {
    const { topLeft, bottomRight } = this.#selection!;
    const fromCoordinate = this.fromIndexCoordinate(topLeft);
    const toCoordinate = this.fromIndexCoordinate(bottomRight);

    if (_notNil(fromCoordinate) && _notNil(toCoordinate)) {
      return {
        startColumnId: fromCoordinate.column,
        startRowId: fromCoordinate.row,
        endColumnId: toCoordinate.column,
        endRowId: toCoordinate.row,
      };
    }

    return null;
  }

  tableValid(): boolean {
    return !this.#cellStateService.hasErrors();
  }

  cellStatusOverview(): Record<DataTableCellStatusType, number> {
    return this.#cellStateService.cellStatusOverview();
  }

  redoEnabled(): boolean {
    return !this.#props.readonly && this.#redoStack.length > 0;
  }

  undoEnabled(): boolean {
    return !this.#props.readonly && this.#undoStack.length > 0;
  }

  rowHeaders(): Array<Omit<DataTableRowHeader, 'displayed'>> {
    return Object.values(this.#rowHeaders).map(({ displayed, ...rest }) => rest);
  }

  rowHeaderWidth(): number {
    return this.displayedRowHeaderIds().length * this.#props.dimensions.cellWidth;
  }

  updateDisplayedRowHeaders(
    displayedRowHeaderIds: Set<DataTableRowHeaderId>,
    options?: DataTableUpdateDisplayedRowHeadersOptions
  ): Promise<void> {
    this.#rowHeaders = Object.values(this.#rowHeaders).reduce(
      (acc, { id, title }) => ({
        ...acc,
        [id]: { id, title, displayed: displayedRowHeaderIds.has(id) },
      }),
      {}
    );
    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableRowHeaderStateChangeEventDetail>(DataTableEvent.RowHeaderStateChange, {
        detail: { displayedRowHeaderIds, rowHeaderWidth: this.rowHeaderWidth() },
      })
    );
    return (options?.persist ?? true)
      ? this.#invokeDataTableSettingsUpdateApi({ display_columns: [...displayedRowHeaderIds] })
      : Promise.resolve();
  }

  async updateColumnGroupExpansion(...actions: Array<DataTableColumnGroupExpansionAction>): Promise<void> {
    this.#ensureNotReadonly();

    const request: DataTableColumnExpansionRequest = {
      columns: actions.map((action) => ({
        api_id: action.column_api_id,
        expanded: action.type === 'expand',
      })),
    };

    try {
      const response = await this.#invokeDataTableColumnExpansionApi(request);
      this.#handleColumnExpansionResponse(response, actions);
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to update column group expansion', {
          cause: error,
          slug: 'datatable-column-expansion-error',
          context: {
            studyApiId: this.#props.studyApiId,
            dataTableId: this.tableId ?? 'unknown',
            actionCount: `${actions.length}`,
            actions: JSON.stringify(actions.map((a) => ({ type: a.type, column_api_id: a.column_api_id }))),
          },
        })
      );

      if (axios.isAxiosError(error)) {
        if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
          errorToast('Network error: Unable to update column expansion. Please check your connection.');
        } else if (error.response?.status === 400) {
          errorToast('Invalid column expansion request. Please refresh and try again.');
        } else if (error.response?.status === 404) {
          errorToast('Column not found. The table structure may have changed. Please refresh.');
        } else if (error.response?.status && error.response.status >= 500) {
          errorToast('Server error while updating column expansion. Please try again later.');
        } else {
          errorToast('Could not update column group expansion. Please try again.');
        }
      } else {
        errorToast('Could not update column group expansion. Please try again.');
      }
      throw error;
    }
  }

  displayedRowHeaderTitles(): Array<string> {
    return this.#getDisplayedRowHeaders().map((header) => header.title);
  }

  displayedRowHeaderIds(): Array<DataTableRowHeaderId> {
    return this.#getDisplayedRowHeaders().map((header) => header.id);
  }

  #getDisplayedRowHeaders(): Array<DataTableRowHeader> {
    return Object.values(this.#rowHeaders).filter((header) => header.displayed);
  }

  #activateSelection(options?: Pick<DataTableCellSelectionChangeEventDetail, 'content' | 'options'>): void {
    if (!this.#props.readonly && _notNil(this.#selection)) {
      this.#dispatchSelectionChange({
        source: 'user',
        scrollTo: false,
        activate: true,
        content: options?.content,
        options: options?.options,
      });
    }
  }

  #dispatchSelectionChange(
    details: Omit<DataTableCellSelectionChangeEventDetail, 'selection'> & { selection?: IDataTableSelection }
  ): void {
    const selection = details.selection ?? this.#selection;
    if (_isNil(selection)) {
      return;
    }

    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableCellSelectionChangeEventDetail>(DataTableEvent.CellSelectionChange, {
        detail: {
          ...details,
          selection: Object.freeze(selection),
        },
      })
    );
  }

  #ensureNotReadonly(): void {
    if (this.#props.readonly) {
      throw new InVivoError('DataTable readonly', { slug: 'datatable-readonly' });
    }
  }

  async moveColumn(columnMoveRequest: DataTableColumnMoveRequest): Promise<DataTableMoveColumnResponse> {
    this.#ensureNotReadonly();
    const result = await this.#invokeDataTableColumMoveApi(columnMoveRequest);

    const updatedColumns = result.columns;
    const updatedRows = result.rows;

    if (_notNil(this.#table)) {
      this.#table.columns = updatedColumns as DataTable['columns'];
      this.#table.rows = updatedRows as DataTable['rows'];
    }

    this.#updateColumnData(updatedColumns);
    this.#rowData = DataTableService.#generateInternalRowData(updatedRows);

    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableStateChangeEventDetail>(DataTableEvent.TableStateChange, {
        detail: {
          type: Object.freeze(new Set<DataTableStateChangeType>(['columns'])),
          ids: Object.freeze(new Set(updatedColumns.map(({ id }) => id))),
        },
      })
    );
    this.#eventEmitter.dispatchEvent(new CustomEvent<DataTableStateChangeEventDetail>(DataTableEvent.MoveColumn));

    return result;
  }

  async addColumn(columnAddRequest: DataTableColumnAddRequest): Promise<Array<DataTableColumn>> {
    this.#ensureNotReadonly();
    if (columnAddRequest?.pk?.from) {
      columnAddRequest.pk.from = DateInputUtils.localDateTimeToISODateTime(columnAddRequest.pk.from);
    }
    const result = await this.#invokeDataTableColumAddApi(columnAddRequest);
    const updatedColumns = [...this.columns, ...result];
    if (_notNil(this.#table)) {
      this.#table.columns = updatedColumns as DataTable['columns'];
    }
    this.#updateColumnData(updatedColumns);
    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableStateChangeEventDetail>(DataTableEvent.TableStateChange, {
        detail: {
          type: Object.freeze(new Set<DataTableStateChangeType>(['columns'])),
          ids: Object.freeze(new Set(result.map(({ id }) => id))),
        },
      })
    );
    // Immediately trigger cell loading for all rows in the new column
    const columnIndex = this.columnCount - 1; // Use leaf column index
    const rowCount = this.rows.length;
    if (rowCount > 0) {
      this.#cellLoadRectOverride$.next(
        new DataTableRectForceLoad({ top: 0, bottom: rowCount - 1, left: columnIndex, right: columnIndex, force: true })
      );
    }
    return result;
  }

  async updateColumn(columnUpdateRequest: DataTableColumnUpdateRequest): Promise<DataTableColumn> {
    this.#ensureNotReadonly();
    if (!isUpdatableColumnType(columnUpdateRequest.type)) {
      throw new InVivoError('This column cannot be updated', { slug: 'invalid-column-update' });
    }
    const result = await this.#invokeDataTableColumnUpdateApi(columnUpdateRequest);
    if (
      ['formula', 'observation', 'measurement', 'timestampBaselineRelative'].includes(result?.type) ||
      ('locked' in columnUpdateRequest && _notNil(columnUpdateRequest.locked))
    ) {
      this.#handleSideEffects(
        [columnUpdateRequest.id],
        this.rows.map((r) => r.id)
      );
      this.validate();
    }
    const allColumns = [...(this.#table?.columns ?? [])].map((existingColumn) =>
      existingColumn.id === result.id ? result : existingColumn
    ) as Array<DataTableColumn>;
    if (_notNil(this.#table)) {
      this.#table.columns = allColumns as DataTable['columns'];
    }
    this.#updateColumnData(allColumns);
    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableStateChangeEventDetail>(DataTableEvent.TableStateChange, {
        detail: {
          type: Object.freeze(new Set<DataTableStateChangeType>(['columns'])),
          ids: Object.freeze(new Set([result.id])),
        },
      })
    );
    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableColumnUpdateEventDetail>(DataTableEvent.ColumnUpdate, { detail: result })
    );
    return result;
  }

  async fillColumn(column_id: DataTableColumnApiId, request: DataTableColumnFillRequestV1): Promise<void> {
    return this.#invokeDataTableColumFillApi(column_id, request).then((response) => {
      this.#handleSideEffects(
        [column_id, ...(response.effects ?? [])],
        this.rows.map((r) => r.id)
      );
      this.validate();
    });
  }

  async addRow(rowAddRequest: DataTableRowAddRequest): Promise<Array<DataTableRow>> {
    this.#ensureNotReadonly();
    const result = await this.#invokeDataTableRowAddApi(rowAddRequest);
    const updatedRows = [...this.rows, ...result];
    if (_notNil(this.#table)) {
      this.#table.rows = updatedRows as DataTable['rows'];
    }
    this.#rowData = DataTableService.#generateInternalRowData(updatedRows);
    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableStateChangeEventDetail>(DataTableEvent.TableStateChange, {
        detail: {
          type: Object.freeze(new Set<DataTableStateChangeType>(['rows'])),
          ids: Object.freeze(new Set(result.map((r) => r.id))),
        },
      })
    );
    // Immediately trigger cell loading for all columns in the new rows
    const bottom = updatedRows.length - 1;
    const top = bottom - (result.length - 1);
    const columnCount = this.columnCount; // Use leaf column count for cell loading
    if (columnCount > 0) {
      this.#cellLoadRectOverride$.next(new DataTableRect({ top, bottom, left: 0, right: columnCount - 1 }));
    }
    return result;
  }

  async removeColumn(columnId: DataTableColumnApiId): Promise<void> {
    this.#ensureNotReadonly();
    const { effects, dependencies } = await this.#invokeDataTableColumRemoveApi(columnId);

    const column: DataTableColumn | undefined = this.columnById(columnId);
    const removableIds: Set<DataTableColumnApiId> =
      column?.type === 'measurement' ? new Set(column?.relations) : new Set();

    // If this is a parent column, add all child columns to removableIds
    this.columns.forEach((col) => {
      if (isChildColumn(col) && col.parent_api_id === columnId) {
        removableIds.add(col.id);
      }
    });

    removableIds.add(columnId);
    this.#handleSideEffects(effects, []);

    this.#cellStateService.clearColumn(...removableIds);
    let updatedColumns = this.columns.filter(({ id }) => !removableIds.has(id));

    dependencies.forEach((dependantColumnId) => {
      this.#cellStateService.clearColumn(dependantColumnId);
      updatedColumns = updatedColumns.filter(({ id }) => id !== dependantColumnId);
    });
    if (_notNil(this.#table)) {
      this.#table.columns = updatedColumns as DataTable['columns'];
    }

    this.#updateColumnData(updatedColumns);
    const emitUndoStateChange = this.undoEnabled() || this.redoEnabled();
    this.#undoStack = [];
    this.#redoStack = [];
    if (emitUndoStateChange) {
      this.#eventEmitter.dispatchEvent(
        new CustomEvent(DataTableEvent.UndoRedoStateChange, {
          detail: { redoEnabled: false, undoEnabled: false },
        })
      );
    }
    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableStateChangeEventDetail>(DataTableEvent.TableStateChange, {
        detail: {
          type: Object.freeze(new Set<DataTableStateChangeType>(['columns'])),
          ids: Object.freeze(new Set([columnId])),
        },
      })
    );
  }

  async removeRow(rowId: DataTableRowApiId): Promise<void> {
    this.#ensureNotReadonly();
    await this.#invokeDataTableRowRemoveApi(rowId);
    this.#cellStateService.clearRow(rowId);
    const updatedRows = this.rows.filter(({ id }) => id !== rowId);
    if (_notNil(this.#table)) {
      this.#table.rows = updatedRows as DataTable['rows'];
    }
    this.#rowData = DataTableService.#generateInternalRowData(updatedRows);
    const emitUndoStateChange = this.undoEnabled() || this.redoEnabled();
    this.#undoStack = [];
    this.#redoStack = [];
    if (emitUndoStateChange) {
      this.#eventEmitter.dispatchEvent(
        new CustomEvent(DataTableEvent.UndoRedoStateChange, {
          detail: { redoEnabled: false, undoEnabled: false },
        })
      );
    }
    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableStateChangeEventDetail>(DataTableEvent.TableStateChange, {
        detail: {
          type: Object.freeze(new Set<DataTableStateChangeType>(['rows'])),
          ids: Object.freeze(new Set([rowId])),
        },
      })
    );
    this.validate();
  }

  static #generateInternalColumnData(columns: Array<DataTableColumn>): InternalColumnData {
    return columns.reduce<InternalColumnData>(
      (acc, column, columnIndex) => {
        acc.idToIndex.set(column.id, columnIndex);
        acc.indexToId.set(columnIndex, column.id);
        acc.columns.set(column.id, column);
        return acc;
      },
      {
        idToIndex: new Map(),
        indexToId: new Map(),
        columns: new Map(),
      }
    );
  }

  /**
   * Handles the response from the column expansion API by updating the displayed state
   * of affected columns and triggering UI updates.
   *
   * **Response Contract:** The backend returns only affected columns with their new displayed state.
   * For expand operations, child columns are returned with displayed=true. For collapse operations,
   * child columns are returned with displayed=false (or a subset may remain visible).
   *
   * **State Updates:**
   * 1. Child columns: Update 'displayed' property based on API response
   * 2. Parent columns: Update 'expanded' property based on action type
   * 3. UI trigger: Dispatch TableStateChange event for reactivity
   *
   * **Edge Cases:**
   * - Empty affectedColumns array: Logs warning but doesn't fail
   * - Mixed visibility: Some children may remain displayed=true even when collapsing
   * - Column type mismatch: Skips updates with defensive logging if column schema doesn't match
   *
   * @param affectedColumns - Array of columns affected by the expansion/collapse operation
   * @param actions - The original actions that triggered the API call
   * @private
   */
  #handleColumnExpansionResponse(
    affectedColumns: DataTableColumnExpansionResponseAffectedColumns,
    actions: Array<DataTableColumnGroupExpansionAction>
  ): void {
    // Validate response structure
    if (!Array.isArray(affectedColumns)) {
      ExceptionHandler.captureException(
        new InVivoError('Invalid column expansion API response: affectedColumns is not an array', {
          slug: 'datatable-column-expansion-invalid-response',
          context: {
            studyApiId: this.#props.studyApiId,
            dataTableId: this.tableId ?? 'unknown',
            responseType: typeof affectedColumns,
            actionCount: `${actions.length}`,
          },
        })
      );
      return; // Early exit to prevent cascading errors
    }

    const affectedColumnsMap = new Map(affectedColumns.map((col) => [col.api_id, col.displayed]));
    const parentExpansionMap = new Map(actions.map((action) => [action.column_api_id, action.type === 'expand']));

    const updatedColumns = this.columns.map((column) => {
      const updates: Record<string, unknown> = {};

      const newDisplayedValue = affectedColumnsMap.get(column.id);
      if (_notNil(newDisplayedValue)) {
        if (hasDisplayedProperty(column)) {
          updates.displayed = newDisplayedValue;
        } else {
          ExceptionHandler.captureException(
            new InVivoError('Skipping displayed update for column without displayed property', {
              slug: 'datatable-column-expansion-skipped-displayed',
              context: {
                columnId: column.id,
                columnType: column.type,
                newDisplayedValue: `${newDisplayedValue}`,
              },
            })
          );
        }
      }

      const newExpandedValue = parentExpansionMap.get(column.id);
      if (_notNil(newExpandedValue)) {
        if (column.type === 'sample') {
          updates.expanded = newExpandedValue;
        } else {
          ExceptionHandler.captureException(
            new InVivoError('Skipping expansion update for non-sample column', {
              slug: 'datatable-column-expansion-skipped-expanded',
              context: {
                columnId: column.id,
                columnType: column.type,
                requestedExpanded: `${newExpandedValue}`,
              },
            })
          );
        }
      }

      return Object.keys(updates).length > 0 ? { ...column, ...updates } : column;
    });

    if (_notNil(this.#table)) {
      this.#table.columns = updatedColumns as DataTable['columns'];
    }

    this.#updateColumnData(updatedColumns);

    this.#eventEmitter.dispatchEvent(
      new CustomEvent<DataTableStateChangeEventDetail>(DataTableEvent.TableStateChange, {
        detail: {
          type: Object.freeze(new Set<DataTableStateChangeType>(['columns'])),
          ids: Object.freeze(new Set(affectedColumns.map((col) => col.api_id as DataTableColumnApiId))),
        },
      })
    );
  }

  static #generateInternalRowData(rows: Array<DataTableRow>): InternalRowData {
    return rows.reduce<InternalRowData>(
      (acc, row, rowIndex) => {
        acc.idToIndex.set(row.id, rowIndex);
        acc.indexToId.set(rowIndex, row.id);
        acc.rows.set(row.id, row);
        return acc;
      },
      { idToIndex: new Map(), indexToId: new Map(), rows: new Map() }
    );
  }

  #processColumnsForGrouping(columns: Array<DataTableColumn>): DataTableColumnGroupingData | null {
    if (_isEmpty(columns)) {
      return null;
    }

    const { parentIds, childrenByParent } = this.#buildParentChildMap(columns);
    const parentColumns = this.#extractParentColumns(parentIds, columns);
    const leafColumns = this.#extractLeafColumns(parentIds, columns);
    const columnGroups = this.#buildColumnGroups(parentColumns, childrenByParent, leafColumns);
    const { columnGroupMap, groupStartSet, groupEndSet } = this.#buildGroupIndexMaps(columnGroups);

    return {
      leafColumns,
      columnGroups,
      columnGroupMap,
      groupStartSet,
      groupEndSet,
    };
  }

  #buildParentChildMap(columns: Array<DataTableColumn>): {
    parentIds: Set<DataTableColumnApiId>;
    childrenByParent: Map<DataTableColumnApiId, Array<DataTableColumn>>;
  } {
    const parentIds = new Set<DataTableColumnApiId>();
    const childrenByParent = new Map<DataTableColumnApiId, Array<DataTableColumn>>();

    for (const col of columns) {
      if (isChildColumn(col)) {
        parentIds.add(col.parent_api_id);
        const siblings = childrenByParent.get(col.parent_api_id) ?? [];
        siblings.push(col);
        childrenByParent.set(col.parent_api_id, siblings);
      }
    }

    return { parentIds, childrenByParent };
  }

  #extractParentColumns(parentIds: Set<DataTableColumnApiId>, columns: Array<DataTableColumn>): Array<DataTableColumn> {
    const columnsById = new Map(columns.map((col) => [col.id, col]));
    return Array.from(parentIds).reduce<Array<DataTableColumn>>((acc, parentId) => {
      const parentCol = columnsById.get(parentId);
      if (_notNil(parentCol)) {
        acc.push(parentCol);
      }
      return acc;
    }, []);
  }

  #extractLeafColumns(parentIds: Set<DataTableColumnApiId>, columns: Array<DataTableColumn>): Array<DataTableColumn> {
    return columns.filter((col) => {
      if (parentIds.has(col.id)) {
        return false;
      }
      if (!hasDisplayedProperty(col)) {
        return true; // No displayed property means visible by default
      }
      return col.displayed !== false;
    });
  }

  #buildColumnGroups(
    parentColumns: Array<DataTableColumn>,
    childrenByParent: Map<DataTableColumnApiId, Array<DataTableColumn>>,
    leafColumns: Array<DataTableColumn>
  ): Array<DataTableColumnGroup> {
    return parentColumns.reduce<Array<DataTableColumnGroup>>((acc, parentCol) => {
      const children = childrenByParent.get(parentCol.id);
      if (_notNil(children) && _isNotEmpty(children)) {
        if (isColumnExpanded(parentCol)) {
          const startIndex = leafColumns.indexOf(children[0]);
          if (startIndex !== -1) {
            acc.push({
              parentColumn: parentCol,
              childColumns: children,
              startIndex,
              colspan: children.length,
            });
          }
        } else {
          const visibleChildren = children.filter((child) => leafColumns.includes(child));
          if (_isNotEmpty(visibleChildren)) {
            const startIndex = leafColumns.indexOf(visibleChildren[0]);
            if (startIndex !== -1) {
              acc.push({
                parentColumn: parentCol,
                childColumns: children,
                startIndex,
                colspan: visibleChildren.length,
              });
            }
          }
        }
      }
      return acc;
    }, []);
  }

  #buildGroupIndexMaps(columnGroups: Array<DataTableColumnGroup>): {
    columnGroupMap: Map<number, DataTableColumnGroup>;
    groupStartSet: Set<number>;
    groupEndSet: Set<number>;
  } {
    const columnGroupMap = new Map<number, DataTableColumnGroup>();
    const groupStartSet = new Set<number>();
    const groupEndSet = new Set<number>();

    for (const group of columnGroups) {
      groupStartSet.add(group.startIndex);
      const groupEndIndex = group.startIndex + group.colspan - 1;
      groupEndSet.add(groupEndIndex);
      for (let i = group.startIndex; i < group.startIndex + group.colspan; i++) {
        columnGroupMap.set(i, group);
      }
    }

    return { columnGroupMap, groupStartSet, groupEndSet };
  }

  getColumnGroupingData(): DataTableColumnGroupingData | null {
    return this.#columnGroupingData;
  }

  isGroupBoundary(columnIndex: DataTableColumnIndex): boolean {
    return this.#columnGroupingData?.groupEndSet.has(columnIndex) ?? false;
  }

  getPinnedColumns(): Readonly<Array<PinnedColumn>> {
    return Object.freeze([...this.#pinnedColumns]);
  }

  #handleSideEffects(columnIds: Array<DataTableColumnApiId>, rowIds: Array<DataTableRowApiId>) {
    of({ columnIds: new Set(columnIds), rowIds: new Set(rowIds) })
      .pipe(
        takeUntil(this.#destroy$),
        mergeMap(({ columnIds, rowIds }) => {
          const [fromRowIndex, toRowIndex] = Array.from(rowIds).reduce(
            (acc, rowId) => {
              const rowIndex = this.#rowData?.idToIndex.get(rowId);
              if (acc[0] > (rowIndex ?? Infinity)) {
                acc[0] = rowIndex ?? Infinity;
              }
              if (acc[1] < (rowIndex ?? 0)) {
                acc[1] = rowIndex ?? 0;
              }
              return acc;
            },
            [this.#rowData?.rows.size ?? Infinity, 0]
          );
          const includeAlerts = Array.from(columnIds).some(
            (columnId) => this.columnById(columnId)?.type === 'measurement'
          );
          if (includeAlerts) {
            columnIds.forEach((columnId) => {
              if (this.columnById(columnId)?.type === 'measurement') {
                (rowIds.size > 0 ? rowIds : [...(this.#rowData?.idToIndex.keys() ?? [])]).forEach((rowId) => {
                  this.#cellAlertService.delete({ column: columnId, row: rowId });
                });
              }
            });
          }

          const fromRowId = this.#rowData?.indexToId.get(fromRowIndex) ?? this.#rowData?.indexToId.get(0);
          const toRowId =
            this.#rowData?.indexToId.get(toRowIndex) ?? this.#rowData?.indexToId.get(this.#rowData?.rows.size ?? 0);
          if (_notNil(fromRowId) && _notNil(toRowId)) {
            return forkJoin(
              [...columnIds].reduce<Array<Observable<Array<DataTableApiLoadCellResponse>>>>((acc, columnId) => {
                acc.push(
                  of({
                    from: { column: columnId, row: fromRowId },
                    to: { column: columnId, row: toRowId },
                    include_alerts: includeAlerts,
                  }).pipe(
                    this.#props.apiService.loadCells(this),
                    catchError((err) => {
                      ExceptionHandler.captureException(
                        new InVivoError('Could not load cells (side effect)', {
                          cause: err,
                          slug: 'datatable-cells-side-effect-load',
                        })
                      );
                      return EMPTY;
                    })
                  )
                );
                return acc;
              }, [])
            );
          }
          return EMPTY;
        })
      )
      .subscribe((cellData) => {
        const { cellUpdateEvent, cellAlertUpdateEvent } = cellData.flat(1).reduce<{
          cellUpdateEvent: DataTableCellUpdateEventDetail;
          cellAlertUpdateEvent: DataTableCellAlertUpdateEventDetail;
        }>(
          (acc, { column_id, row_id, cell: { value }, updated_at, alerts }) => {
            const coordinate: DataTableCellIdCoordinate = { column: column_id, row: row_id };
            const ref = toCellRef(coordinate);
            acc.cellAlertUpdateEvent.cellAlerts[ref] = this.#cellAlertService.ingest(coordinate, alerts);
            if (this.#getCellData(coordinate)?.value !== value) {
              this.#setCellData(coordinate, { value, updated_at });
              acc.cellUpdateEvent[ref] = { value, updated_at };
            }
            return acc;
          },
          { cellUpdateEvent: {}, cellAlertUpdateEvent: { cellAlerts: {}, trigger: 'cell-load' } }
        );
        this.#eventEmitter.dispatchEvent(new CustomEvent(DataTableEvent.CellUpdate, { detail: cellUpdateEvent }));
        this.#eventEmitter.dispatchEvent(
          new CustomEvent(DataTableEvent.CellAlertUpdate, { detail: cellAlertUpdateEvent })
        );
      });
  }

  #isColumnReadonly(column: Nullable<DataTableColumn>): boolean {
    if (_isNil(column) || column?.read_only === true || column?.locked === true) {
      return true;
    }
    if (column.type === 'measurement' && (column.measurement.is_calculated ?? false)) {
      return true;
    }
    return isColumnTypeReadonly(column.type);
  }

  featureFlagEnabled(featureFlag: keyof DataTableFeatureFlags): boolean {
    return this.#props.featureFlags?.[featureFlag] ?? false;
  }
}
